import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { FiUser, FiMail, FiPhone, FiCreditCard, FiCalendar, FiMapPin, FiBriefcase, FiEdit2, FiSave, FiX, FiBell, FiBellOff } from 'react-icons/fi';
import { SkeletonProfileBlock } from '../common/Skeleton.jsx';
import { useAuthStore } from '../../store/authStore';
import { usePushNotifications } from '../PushNotifications/PushNotifications';
import './MyProfile.css';

const MyProfile = () => {
  const { loadUser } = useAuthStore();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingPic, setUploadingPic] = useState(false);
  const [formData, setFormData] = useState({});
  const picInputRef = useRef();
  const { subscribed, loading: notifLoading, subscribe, unsubscribe } = usePushNotifications();

  useEffect(() => { fetchProfile(); }, []);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/profile/my-profile');
      setProfile(response.data.profile);
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleEditOpen = () => {
    setFormData({
      firstName: profile?.firstName || '',
      lastName: profile?.lastName || '',
      fatherName: profile?.fatherName || '',
      dateOfBirth: profile?.dateOfBirth ? profile.dateOfBirth.split('T')[0] : '',
      gender: profile?.gender || '',
      personalEmail: profile?.personalEmail || '',
      phoneNumber: profile?.phoneNumber || '',
      cnicNumber: profile?.cnicNumber || '',
      currentAddress: profile?.currentAddress || '',
      permanentAddress: profile?.permanentAddress || '',
    });
    setEditMode(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/profile/update', formData);
      toast.success('Profile updated!');
      await fetchProfile();
      await loadUser();
      setEditMode(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handlePicChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingPic(true);
    try {
      const fd = new FormData();
      fd.append('profilePicture', file);
      await api.post('/profile/upload-picture', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Profile picture updated!');
      await fetchProfile();
      await loadUser();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to upload picture');
    } finally {
      setUploadingPic(false);
      e.target.value = '';
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Not provided';
    return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) return <SkeletonProfileBlock />;

  return (
    <div className="my-profile-container">
      <div className="profile-header">
        <div>
          <h1>👤 My Profile</h1>
          <p>View and update your profile information</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {!editMode ? (
            <button className="upload-btn" onClick={handleEditOpen}>
              <FiEdit2 /> Edit Profile
            </button>
          ) : (
            <>
              <button className="upload-btn secondary" onClick={() => setEditMode(false)}>
                <FiX /> Cancel
              </button>
              <button className="save-btn" onClick={handleSave} disabled={saving}>
                <FiSave /> {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Profile Picture Section */}
      <div className="profile-card">
        <h2>Profile Picture</h2>
        <div className="profile-picture-section">
          <div
            className="profile-picture-preview clickable-avatar"
            onClick={() => picInputRef.current.click()}
            title="Click to change profile picture"
          >
            {profile?.profilePicture ? (
              <img src={profile.profilePicture} alt="Profile" />
            ) : (
              <div className="profile-avatar-large">
                {profile?.firstName?.[0]}{profile?.lastName?.[0]}
              </div>
            )}
            <div className="avatar-overlay">
              {uploadingPic ? 'Uploading...' : '📷 Change'}
            </div>
          </div>
          <input
            ref={picInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handlePicChange}
          />
          <p className="upload-hint">Click on the picture to change it. JPG, PNG or GIF. Max 5MB.</p>
        </div>
      </div>

      {/* Personal Information */}
      <div className="profile-card">
        <h2>Personal Information</h2>
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label"><FiUser /> Full Name</span>
            {editMode ? (
              <div style={{ display: 'flex', gap: '8px' }}>
                <input className="inline-edit-input" value={formData.firstName} onChange={e => setFormData({ ...formData, firstName: e.target.value })} placeholder="First name" />
                <input className="inline-edit-input" value={formData.lastName} onChange={e => setFormData({ ...formData, lastName: e.target.value })} placeholder="Last name" />
              </div>
            ) : (
              <span className="info-value">{profile?.firstName} {profile?.lastName}</span>
            )}
          </div>
          <div className="info-item">
            <span className="info-label"><FiUser /> Father Name</span>
            {editMode ? (
              <input className="inline-edit-input" value={formData.fatherName} onChange={e => setFormData({ ...formData, fatherName: e.target.value })} placeholder="Father name" />
            ) : (
              <span className="info-value">{profile?.fatherName || 'Not provided'}</span>
            )}
          </div>
          <div className="info-item">
            <span className="info-label"><FiCalendar /> Date of Birth</span>
            {editMode ? (
              <input type="date" className="inline-edit-input" value={formData.dateOfBirth} onChange={e => setFormData({ ...formData, dateOfBirth: e.target.value })} />
            ) : (
              <span className="info-value">{formatDate(profile?.dateOfBirth)}</span>
            )}
          </div>
          <div className="info-item">
            <span className="info-label"><FiUser /> Gender</span>
            {editMode ? (
              <select className="inline-edit-input" value={formData.gender} onChange={e => setFormData({ ...formData, gender: e.target.value })}>
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            ) : (
              <span className="info-value">{profile?.gender ? profile.gender.charAt(0).toUpperCase() + profile.gender.slice(1) : 'Not provided'}</span>
            )}
          </div>
          <div className="info-item">
            <span className="info-label"><FiMail /> Company Email</span>
            <span className="info-value">{profile?.email}</span>
          </div>
          <div className="info-item">
            <span className="info-label"><FiMail /> Personal Email</span>
            {editMode ? (
              <input type="email" className="inline-edit-input" value={formData.personalEmail} onChange={e => setFormData({ ...formData, personalEmail: e.target.value })} placeholder="Enter personal email" />
            ) : (
              <span className="info-value">{profile?.personalEmail || 'Not provided'}</span>
            )}
          </div>
          <div className="info-item">
            <span className="info-label"><FiPhone /> Phone Number</span>
            {editMode ? (
              <input type="text" className="inline-edit-input" value={formData.phoneNumber} onChange={e => setFormData({ ...formData, phoneNumber: e.target.value })} placeholder="Enter phone number" />
            ) : (
              <span className="info-value">{profile?.phoneNumber || 'Not provided'}</span>
            )}
          </div>
          <div className="info-item">
            <span className="info-label"><FiCreditCard /> CNIC Number</span>
            {editMode ? (
              <input type="text" className="inline-edit-input" value={formData.cnicNumber} onChange={e => setFormData({ ...formData, cnicNumber: e.target.value })} placeholder="Enter CNIC number" />
            ) : (
              <span className="info-value">{profile?.cnicNumber || 'Not provided'}</span>
            )}
          </div>
        </div>
      </div>

      {/* Address Information */}
      <div className="profile-card">
        <h2>Address Information</h2>
        <div className="info-grid">
          <div className="info-item full-width">
            <span className="info-label"><FiMapPin /> Current Address</span>
            {editMode ? (
              <textarea
                className="inline-edit-input"
                rows={2}
                value={formData.currentAddress}
                onChange={e => setFormData({ ...formData, currentAddress: e.target.value })}
                placeholder="Enter current address"
              />
            ) : (
              <span className="info-value">{profile?.currentAddress || 'Not provided'}</span>
            )}
          </div>
          <div className="info-item full-width">
            <span className="info-label"><FiMapPin /> Permanent Address</span>
            {editMode ? (
              <textarea
                className="inline-edit-input"
                rows={2}
                value={formData.permanentAddress}
                onChange={e => setFormData({ ...formData, permanentAddress: e.target.value })}
                placeholder="Enter permanent address"
              />
            ) : (
              <span className="info-value">{profile?.permanentAddress || 'Not provided'}</span>
            )}
          </div>
        </div>
      </div>

      {/* Employment Information */}
      <div className="profile-card">
        <h2>Employment Information</h2>
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label"><FiBriefcase /> Employee ID</span>
            <span className="info-value">{profile?.employeeId || 'Not assigned'}</span>
          </div>
          <div className="info-item">
            <span className="info-label"><FiBriefcase /> Designation</span>
            <span className="info-value">{profile?.designation || 'Not assigned'}</span>
          </div>
          <div className="info-item">
            <span className="info-label"><FiCalendar /> Joining Date</span>
            <span className="info-value">{formatDate(profile?.joiningDate || profile?.createdAt)}</span>
          </div>
          <div className="info-item">
            <span className="info-label"><FiBriefcase /> Employment Type</span>
            <span className="info-value">{profile?.employmentType ? profile.employmentType.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : 'Not specified'}</span>
          </div>
        </div>
      </div>

      {/* CNIC Images */}
      <div className="profile-card">
        <h2>CNIC Images</h2>
        <div className="cnic-images-grid">
          <div className="cnic-section">
            <h3>CNIC Front</h3>
            <div className="cnic-preview">
              {profile?.cnicFrontImage ? (
                <img src={profile.cnicFrontImage} alt="CNIC Front" />
              ) : (
                <div className="cnic-placeholder">
                  <FiCreditCard size={48} />
                  <p>No image uploaded</p>
                </div>
              )}
            </div>
          </div>
          <div className="cnic-section">
            <h3>CNIC Back</h3>
            <div className="cnic-preview">
              {profile?.cnicBackImage ? (
                <img src={profile.cnicBackImage} alt="CNIC Back" />
              ) : (
                <div className="cnic-placeholder">
                  <FiCreditCard size={48} />
                  <p>No image uploaded</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Account Information */}
      <div className="profile-card">
        <h2>Account Information</h2>
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Username</span>
            <span className="info-value">@{profile?.username}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Role</span>
            <span className="info-value">{profile?.role?.replace(/_/g, ' ').toUpperCase()}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Department</span>
            <span className="info-value">{profile?.department?.toUpperCase()}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Joining Date</span>
            <span className="info-value">
              {new Date(profile?.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </span>
          </div>
        </div>
      </div>

      {/* Notification Settings */}
      {'Notification' in window && 'serviceWorker' in navigator && (
        <div className="profile-card">
          <h2>Notification Settings</h2>
          <div className="notif-toggle-row">
            <div className="notif-toggle-info">
              {subscribed ? <FiBell size={22} className="notif-icon on" /> : <FiBellOff size={22} className="notif-icon off" />}
              <div>
                <p className="notif-title">Push Notifications</p>
                <p className="notif-desc">
                  {subscribed ? 'You will receive alerts for messages and calls.' : 'Enable to get alerts for messages and calls.'}
                </p>
              </div>
            </div>
            <button
              className={`notif-toggle-btn ${subscribed ? 'active' : ''}`}
              onClick={subscribed ? unsubscribe : subscribe}
              disabled={notifLoading}
            >
              {notifLoading ? '...' : subscribed ? 'Turn Off' : 'Turn On'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyProfile;
